// IND-004: «Datos a fecha» del Panel de planta.
// IND-012: los avisos ya no ocupan la vista principal: van en la ventana del
// botón «Avisos de datos · N».
// IND-015: «Datos a fecha» sale de la vista principal y pasa a un botón con
// ventana; los avisos se agrupan por tipo.
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { EventRounded, ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { getPlantDataFreshness, getPlantDataReview, getRecountStatus } from '../services/industrialCostService';
import type { PlantDataFreshness } from '../services/industrialCostService';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import { formatEsNumber } from '../utils/numberFormat';
import { groupNoticesByType } from '../utils/plantPanel';
import { loadBreakdownText } from '../utils/recountProposal';
import { RECOUNT_STATUS_QUERY_KEY } from './IndustrialCostDataSourcesBlocks';
import type { NoticeGroup } from '../utils/plantPanel';
import { LEVEL_STYLE, NoticeCard, PLANT_DATA_REVIEW_QUERY_KEY } from './IndustrialCostFileReviewPanel';
import { MarkHolidayButton } from './IndustrialCostPlantProductionYear';

export const PLANT_DATA_FRESHNESS_QUERY_KEY = ['industrial-cost-plant-data-freshness'];

export function usePlantDataFreshness() {
  return useQuery({
    queryKey: PLANT_DATA_FRESHNESS_QUERY_KEY,
    queryFn: getPlantDataFreshness,
    staleTime: 60_000,
    retry: false,
  });
}

/** Albaranes, OF y compras son las descargas del día del ERP: mismo día si
 * las tres fechas coinciden (producción va siempre un poco por detrás). */
export function freshnessDayState(freshness: PlantDataFreshness | undefined): 'same' | 'different' | 'none' {
  if (!freshness) return 'none';
  const erpDates = freshness.items
    .filter((item) => ['direct-inputs', 'manufacturing-orders', 'purchase-orders'].includes(item.source_key))
    .map((item) => item.data_date)
    .filter(Boolean);
  if (erpDates.length < 3) return 'none';
  return new Set(erpDates).size === 1 ? 'same' : 'different';
}

const DAY_STATE_STYLE = {
  same: { color: '#86efac', border: 'rgba(74,222,128,0.5)', label: 'Datos a fecha' },
  different: { color: '#fcd34d', border: 'rgba(245,158,11,0.5)', label: 'Datos a fecha · ficheros de días distintos' },
  none: { color: '#94a3b8', border: 'rgba(148,163,184,0.4)', label: 'Datos a fecha · sin datos' },
} as const;

/** Botón de la cabecera: verde si los ficheros son del mismo día; ámbar si no;
 * gris si faltan fechas. */
export function PlantDataFreshnessButton({ onClick }: { onClick: () => void }) {
  const freshnessQuery = usePlantDataFreshness();
  const style = DAY_STATE_STYLE[freshnessDayState(freshnessQuery.data)];
  return (
    <Button
      variant="outlined"
      startIcon={<EventRounded />}
      onClick={onClick}
      sx={{ textTransform: 'none', color: style.color, borderColor: style.border }}
    >
      {freshnessQuery.isLoading ? 'Datos a fecha · …' : style.label}
    </Button>
  );
}

export function PlantDataFreshnessDialog({
  open,
  onClose,
  kpiNote,
  onOpenFileReview,
}: {
  open: boolean;
  onClose: () => void;
  /** Texto técnico de los indicadores («Producción usada en KPIs: …»). */
  kpiNote: string;
  onOpenFileReview?: () => void;
}) {
  const freshnessQuery = usePlantDataFreshness();
  const freshness = freshnessQuery.data;
  const dayState = freshnessDayState(freshness);
  // IND-018: fecha del recuento y desglose de su última carga.
  const recountQuery = useQuery({
    queryKey: RECOUNT_STATUS_QUERY_KEY,
    queryFn: getRecountStatus,
    staleTime: 60_000,
    retry: false,
    enabled: open,
  });
  const recount = recountQuery.data;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Datos a fecha</DialogTitle>
      <DialogContent dividers>
        {freshnessQuery.isLoading ? (
          <Alert severity="info" variant="outlined">Leyendo las fechas de los ficheros…</Alert>
        ) : !freshness ? (
          <Alert severity="error" variant="outlined">No se pudieron cargar las fechas de los datos.</Alert>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ color: DAY_STATE_STYLE[dayState].color, fontSize: 13, fontWeight: 800 }}>
              {dayState === 'same'
                ? 'Albaranes, OF y compras son del mismo día.'
                : dayState === 'different'
                  ? 'Albaranes, OF y compras son de días distintos.'
                  : 'Faltan fechas de albaranes, OF o compras.'}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              {freshness.items.map((item) => {
                const day = formatDataDay(item.data_date);
                const extra =
                  item.source_key === 'production' && item.days_with_production
                    ? `${formatEsNumber(item.days_with_production)} días con producción desde el recuento`
                    : item.source_key === 'direct-inputs' && item.last_entry_date
                      ? `último albarán ${formatDataDay(item.last_entry_date)}`
                      : null;
                return (
                  <Box key={item.source_key} sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 800 }}>{item.label}</Typography>
                    <Typography sx={{ color: day ? '#f8fafc' : '#64748b', fontSize: 14, fontWeight: 900 }}>
                      {day ?? 'Sin datos'}
                    </Typography>
                    {item.data_date_kind_label ? (
                      <Typography sx={{ color: '#94a3b8', fontSize: 11 }}>
                        {item.data_date_kind_label}
                        {extra ? ` · ${extra}` : ''}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Box>

            {recount?.count_date ? (
              <Typography sx={{ color: '#cbd5e1', fontSize: 12 }}>
                {`Recuento del ${formatDataDay(recount.count_date)}`}
                {recount.sheet_name ? ` · hoja ${recount.sheet_name}` : ''}
                {loadBreakdownText(recount.breakdown) ? ` · ${loadBreakdownText(recount.breakdown)}` : ''}
              </Typography>
            ) : null}

            {freshness.warnings.length > 0 ? (
              <Stack spacing={0.75}>
                <Typography sx={{ color: '#f8fafc', fontSize: 13, fontWeight: 800 }}>Avisos de coherencia</Typography>
                {freshness.warnings.map((warning, index) => (
                  <Alert key={`${warning.code}-${index}`} severity={warning.severity} variant="outlined">
                    {warning.message}
                    {/* IND-022: cada día se puede marcar como festivo. */}
                    {warning.code === 'workdays_without_production' && (warning.dates ?? []).length > 0 ? (
                      <Stack spacing={0.25} sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto' }}>
                        {(warning.dates ?? []).map((day) => (
                          <Stack key={day} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography sx={{ fontSize: 12, minWidth: 84 }}>{formatDataDay(day)}</Typography>
                            <MarkHolidayButton day={day} />
                          </Stack>
                        ))}
                      </Stack>
                    ) : null}
                  </Alert>
                ))}
              </Stack>
            ) : null}

            <Typography sx={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'pre-line' }}>{kpiNote}</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {onOpenFileReview ? (
          <Button
            onClick={() => {
              onClose();
              onOpenFileReview();
            }}
            sx={{ textTransform: 'none' }}
          >
            Datos de planta
          </Button>
        ) : null}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function usePlantDataNotices() {
  return useQuery({
    queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
    queryFn: getPlantDataReview,
    staleTime: 60_000,
    retry: false,
  });
}

function NoticeGroupRow({ group }: { group: NoticeGroup }) {
  const [open, setOpen] = useState(false);
  const style = LEVEL_STYLE[group.level];
  return (
    <Box
      sx={{
        border: `1px solid ${style.color}55`,
        borderLeft: `4px solid ${style.color}`,
        borderRadius: 2,
        bgcolor: 'rgba(2,6,23,0.35)',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.25,
          border: 0,
          bgcolor: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: 14 }}>
            {group.name} · {formatEsNumber(group.count)}
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
            Se corrige en {group.fileName ?? group.fileKey}
            {group.documents > 0 ? ` · ${formatEsNumber(group.documents)} documentos afectados` : ''}
          </Typography>
        </Box>
        <Chip size="small" color={style.muiColor} variant="outlined" label={style.label} />
        {open ? <ExpandLessRounded /> : <ExpandMoreRounded />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ p: 1.25, pt: 0 }}>
          {group.notices.map((notice) => (
            <NoticeCard key={notice.id} notice={notice} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export function PlantDataNoticesDialog({
  open,
  onClose,
  onOpenFileReview,
}: {
  open: boolean;
  onClose: () => void;
  onOpenFileReview?: () => void;
}) {
  const reviewQuery = usePlantDataNotices();
  const groups = groupNoticesByType(reviewQuery.data?.notices ?? []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Avisos de datos</DialogTitle>
      <DialogContent dividers>
        {reviewQuery.isLoading ? (
          <Alert severity="info" variant="outlined">Revisando los ficheros cargados…</Alert>
        ) : reviewQuery.isError ? (
          <Alert severity="error" variant="outlined">No se pudieron cargar los avisos.</Alert>
        ) : groups.length === 0 ? (
          <Alert severity="success" variant="outlined">No hay avisos en los ficheros cargados.</Alert>
        ) : (
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Un aviso por tipo. Primero lo que bloquea; dentro de cada nivel, lo que afecta a más documentos.
              Pulsa un tipo para ver qué falta, a qué afecta y qué hacer.
            </Typography>
            {groups.map((group) => (
              <NoticeGroupRow key={group.key} group={group} />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {onOpenFileReview ? (
          <Button
            onClick={() => {
              onClose();
              onOpenFileReview();
            }}
            sx={{ textTransform: 'none' }}
          >
            Ir a «Revisa tus ficheros»
          </Button>
        ) : null}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
